import type { FunctionComponent } from 'react';
import { useState } from 'react';
import {
  Button,
  Modal,
  paletteHelper,
  spacingHelper,
  Stack,
  Text,
  MOBILE_MAX,
} from '@property.com.au/ui-kit';
import styled, { useTheme } from 'styled-components';
import type { PropertyRelationship } from '../PropertyRelationship';
import { convertHexStringToRGBA } from '../../../app-kit/helpers/convertHexStringToRGBA';
import type {
  AerialImage,
  ListingImage,
} from '../UpdateRelationshipModalContent/subcomponents/FollowPropertyImage/FollowPropertyImage.types';
import { UpdateRelationshipModalContent } from '../UpdateRelationshipModalContent/UpdateRelationshipModalContent';
import { LoadingDots } from '../../../app-kit/components/LoadingDots/LoadingDots';
import { ModalDialog } from '../../ModalDialog/ModalDialog';
import { getCssVariableValue } from '../../../app-kit/helpers/getCssVariableValue';

const StyledButton = styled(Button)`
  flex-grow: 1;
`;

const StyledUnfollowButton = styled(StyledButton)`
  color: ${paletteHelper('textWarning')};
`;

const StyledStack = styled(Stack)`
  margin-right: ${spacingHelper('large')};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${spacingHelper('extraSmall')};
`;

interface UpdatePropertyRelationshipModalProps {
  title: string;
  subtitle: string;
  image?: ListingImage | AerialImage | null;
  propertyRelationship: PropertyRelationship;
  opened: boolean;
  onClose: () => void;
  onSubmitUpdate: (propertyRelationship: PropertyRelationship) => void;
  onSubmitUnfollow: () => void;
  loading: boolean;
  defaultSelection?: PropertyRelationship;
}

export const UpdatePropertyRelationshipModal: FunctionComponent<
  UpdatePropertyRelationshipModalProps
> = ({
  title,
  subtitle,
  image,
  propertyRelationship,
  opened,
  onClose,
  onSubmitUpdate,
  onSubmitUnfollow,
  loading,
  defaultSelection,
}) => {
  const theme = useTheme();
  const colourValue = getCssVariableValue(theme.palette.backgroundOverlayStatic);
  const backgroundColor = convertHexStringToRGBA(colourValue, 0.3);

  const [currentPropertyRelationship, setCurrentPropertyRelationship] =
    useState<PropertyRelationship>(defaultSelection ?? propertyRelationship);

  const onPropertyRelationSelectionChange = (selected: PropertyRelationship) => {
    setCurrentPropertyRelationship(selected);
  };

  return (
    <Modal
      opened={opened}
      onRequestClose={onClose}
      getAppElement={() => '#argonaut-wrapper'}
      reactModalProps={{
        style: {
          overlay: {
            backgroundColor,
          },
        },
      }}
    >
      <ModalDialog
        autoFullScreen
        fullScreenMediaQuery={`(max-width: ${MOBILE_MAX}px)`}
        onClose={onClose}
        header={
          <StyledStack gap="0.125rem">
            <Text as="h2" variant="subtitle01">
              {title}
            </Text>
            <Text as="h3" variant="body01">
              {subtitle}
            </Text>
          </StyledStack>
        }
        footer={
          <ButtonGroup>
            {loading ? (
              <Button
                data-testid="update-relationship-modal-loader-button"
                sizeVariant="medium"
                onClick={() => null}
                fullWidth
                disabled
              >
                <LoadingDots />
              </Button>
            ) : (
              <>
                <StyledUnfollowButton
                  sizeVariant="medium"
                  variant="outline-secondary"
                  onClick={() => void onSubmitUnfollow()}
                >
                  Unfollow
                </StyledUnfollowButton>
                <StyledButton
                  sizeVariant="medium"
                  disabled={currentPropertyRelationship === propertyRelationship}
                  onClick={() => void onSubmitUpdate(currentPropertyRelationship)}
                >
                  Update relationship
                </StyledButton>
              </>
            )}
          </ButtonGroup>
        }
      >
        <UpdateRelationshipModalContent
          image={image}
          propertyRelationship={currentPropertyRelationship}
          privacyLinkOutboundLinkType="set-relationship-modal-privacy-link"
          onSelectionChange={onPropertyRelationSelectionChange}
        />
      </ModalDialog>
    </Modal>
  );
};
